import { z } from 'zod';
import { UI_MODES } from '../constants/setting.constant';
import { objectIdSchema } from './common.schema';

/**
 * ─── LAYER: shared schema ────────────────────────────────────────────────────────
 *
 * THE SYSTEM-WIDE SETTINGS STORE'S WIRE SHAPE. [C1h] §1
 *
 * ⚠️ THERE IS NO GENERIC `PUT /settings/:key` TAKING A FREE-FORM STRING, AND THAT IS THE
 * WHOLE OF WHY THIS FILE HAS ONE SCHEMA PER KEY RATHER THAN ONE FOR ALL OF THEM.
 *
 * A generic key/value endpoint validates the KEY and not the VALUE, so "let them set one
 * more thing" becomes a one-character diff inside a switch — which is verbatim the
 * argument `dashboardLayout.model.ts` makes for two named columns over one bag keyed
 * `<A>#<B>#<FIELD>`. The STORAGE is generic, because that is what makes key #2 a write
 * rather than a migration; the CONTRACT is not.
 */

/**
 * ⚠️ THE ONLY SETTING ON THE WIRE, AND IT IS READ THROUGH `GET /auth/config`.
 *
 * That endpoint is public, pre-authentication and schema-validated, and until [C1h] it had
 * ZERO production consumers — the login screen reads `OTP_CODE_LENGTH` as a compile-time
 * constant instead. It is the right carrier because the UI mode has to be known before a
 * token exists: the login screen is painted in it.
 */
export const uiModeSettingSchema = z.object({
  uiMode: z.enum(UI_MODES),
});

/**
 * The write. `PUT /settings/ui-mode`, behind `settings.write` (SUPER_ADMIN).
 *
 * ⚠️ NO `officerId` AND NO SCOPE. §1d: one value, everyone sees it. There is no personal
 * override and no "remember my choice", because a switch the client can throw for the whole
 * commissionerate is defeated the moment one operator can opt out of it.
 */
export const updateUiModeSchema = uiModeSettingSchema;

/**
 * ⚠️ [UX1 §8] KEY #2, AND IT GETS ITS OWN SCHEMA — WHICH IS THE POINT OF THE HEADER ABOVE.
 *
 * A generic `PUT /settings/:key` taking a free-form string would validate the KEY and not the
 * VALUE, so "let them set one more thing" becomes a one-character diff inside a switch. Two
 * near-identical schemas is the accepted cost of not having that, exactly as two near-identical
 * service functions are — say so, or the next reviewer tidies them into one.
 *
 * ⚠️ THE FIELD IS A BOOLEAN ON THE WIRE AND A STRING IN MONGO. `settings.value` is one column
 * serving every key, so `asNodalAuthorSetting` narrows on the way out; the contract does not
 * inherit the storage's shape.
 */
export const arrangementAuthoringSettingSchema = z.object({
  nodalsMayAuthorArrangements: z.boolean(),
});

/**
 * The write. `PUT /settings/arrangement-authoring`, behind `settings.write` (SUPER_ADMIN).
 *
 * ⚠️ NO SCOPE AND NO ORG UNIT. §8's value is one policy for the whole deployment, like the UI
 * mode — a per-division override would be a capability table with a second home.
 */
export const updateArrangementAuthoringSchema = arrangementAuthoringSettingSchema;

/** What the write answers with — the stored value and who last moved it. */
export const settingRecordSchema = z.object({
  key: z.string(),
  value: z.string(),
  updatedByOfficerId: objectIdSchema.nullable(),
  updatedAt: z.iso.datetime(),
});
